import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Link2, UserPlus } from 'lucide-react';
import { mensajeDeError } from '../lib/mensajeDeError';
import { loQueFaltaEnElAlta, seRenuevaSolo, pagaEnTramos, PAGO_EN_TRAMOS } from '../lib/loQueFaltaEnElAlta';
import { euros } from '../lib/precios';
import { hoyLocal, pintarDia } from '../lib/horaEspana';
import { planesParaElegir } from '../lib/planesParaElegir';

// DAR DE ALTA A UN CLIENTE DESDE EL PANEL (Jesús, reunión del 25-09: «te voy a dar el email
// y le haces accesible; una cuenta de cliente normal»). Nombre, correo, plan y hasta cuándo
// tiene acceso (o de cortesía). La contraseña es temporal y sale UNA vez, como al
// restablecerla: se copia y se le pasa por WhatsApp.
//
// O COBRANDO ANTES (15-10, Francisco): «Cobrar con enlace» saca el enlace de pago y, cuando
// lo paga, la cuenta se crea sola con el plan y sale el correo del alta (backend
// `core/alta_al_pagar`). La fecha de acceso que se propone es la del servidor
// (`/admin/clients/alta/fechas`): la misma cuenta que pone el ciclo al darle el plan.
// Con enlace de pago por defecto (16-10, hoja del lanzamiento, tarea 2: el botón es «Crear enlace de
// pago»). El alta a mano, la de cortesía o la de un pago hecho por fuera, es desmarcarlo.
// LA RENOVACIÓN AUTOMÁTICA VA SIEMPRE PUESTA (batería de Jesús, prueba 1.5, 03-11): «viene
// marcada por defecto y no se puede desmarcar». Por eso `recurrente` nace a true y la casilla
// está bloqueada.
const VACIO = { nombre: '', email: '', plan: '', hasta: '', cortesia: false, conEnlace: true, recurrente: true, pagoSemanas: '12' };
// «lunes 26 de octubre de 2026», sin la coma que pone el navegador tras el día de la semana.
const largo = (dia) => pintarDia(dia, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(',', '');
// Un día 'AAAA-MM-DD' movido `n` días (negativo, hacia atrás).
const masDias = (dia, n) => {
    const [a, m, d] = String(dia).split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, d + n, 12)).toISOString().slice(0, 10);
};
//: Cuántos días antes del final del acceso se cobra la renovación (prueba 1.4: «se pedirá seis
//: días antes del final del acceso»). Es el lunes de su última semana pagada, la que acaba en
//: domingo: lo que ya hace el cobro (`backend/core/cobro_recurrente.py`).
const DIAS_ANTES_DE_RENOVAR = 6;
const DarDeAlta = ({ onCreado }) => {
    const { api, planCatalog } = useAuth();
    const navigate = useNavigate();
    const [abierto, setAbierto] = useState(false);
    const [form, setForm] = useState(VACIO);
    const [fechas, setFechas] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [hecho, setHecho] = useState(null);
    const planes = planesParaElegir(planCatalog, null).nuevos;
    const campo = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    // Qué falta para poder dar de alta, dicho en una frase; null si no falta nada. La regla
    // vive en lib/loQueFaltaEnElAlta y el botón se enciende con ella.
    const falta = loQueFaltaEnElAlta(form, hoyLocal());
    // «Pago 4 semanas / Pago 12 semanas» (Francisco, 16-10): solo en los planes que lo admiten,
    // con el importe del tramo que da el servidor (`/plans`, `pago_en_tramos`).
    const tramo = planCatalog?.[form.plan]?.pago_en_tramos || null;
    const enTramos = pagaEnTramos(planCatalog, form.plan, form.pagoSemanas);
    const seRenueva = seRenuevaSolo({ ...form, pagoSemanas: enTramos ? PAGO_EN_TRAMOS : '12' });
    // Hasta cuándo tiene acceso si paga hoy, y cuándo se le cobra la renovación. En tramos, lo
    // pagado son 4 semanas desde que empieza; si no, lo que dice el servidor para el plan.
    const ultimoDia = fechas?.empieza
        ? (enTramos ? masDias(fechas.empieza, Number(PAGO_EN_TRAMOS) * 7 - 1) : fechas.hasta)
        : null;
    const renueva = ultimoDia ? masDias(ultimoDia, -DIAS_ANTES_DE_RENOVAR) : null;

    const cerrar = () => {
        setAbierto(false);
        setHecho(null);
        setForm(VACIO);
        setFechas(null);
    };

    // Al elegir el plan, las fechas que le tocarían entrando hoy; la de acceso se propone.
    useEffect(() => {
        if (!abierto || !form.plan) { setFechas(null); return undefined; }
        let vivo = true;
        api.get('/admin/clients/alta/fechas', { params: { plan: form.plan } })
            .then((r) => {
                if (!vivo) return;
                setFechas(r.data);
                setForm((f) => ({ ...f, hasta: r.data?.hasta || f.hasta }));
            })
            .catch((err) => { console.error('No se pudieron pedir las fechas del alta', err); if (vivo) setFechas(null); });
        return () => { vivo = false; };
    }, [abierto, form.plan, api]);

    const crear = async () => {
        setGuardando(true);
        try {
            // Con enlace, la fecha solo viaja si se ha corregido la sugerida: sin tocarla, la
            // calcula el pago el día que pague, que puede no ser hoy.
            const corregida = !seRenueva && form.hasta && form.hasta !== fechas?.hasta ? form.hasta : undefined;
            const r = form.conEnlace
                ? await api.post('/admin/clients/alta-con-enlace', {
                    nombre: form.nombre, email: form.email, plan: form.plan, recurrente: seRenueva,
                    pago_semanas: enTramos ? Number(PAGO_EN_TRAMOS) : undefined, hasta: corregida })
                : await api.post('/admin/clients/alta', form);
            setHecho({ ...r.data, nombre: form.nombre.trim() || r.data.email, conEnlace: form.conEnlace });
            onCreado && onCreado();
        } catch (err) {
            toast.error(mensajeDeError(err, form.conEnlace ? 'No se pudo sacar el enlace de pago' : 'No se pudo dar de alta'));
        } finally {
            setGuardando(false);
        }
    };

    const copiar = () => navigator.clipboard.writeText(hecho.conEnlace ? hecho.url : hecho.temp_password)
        .then(() => toast.success(hecho.conEnlace ? 'Enlace copiado' : 'Contraseña copiada')).catch(() => {});

    const caja = 'w-full bg-[#0A0A0A] border border-[#333] text-white text-sm rounded-lg px-2 py-2 mt-1';
    return (
        <>
            <Button onClick={() => setAbierto(true)} data-testid="dar-de-alta"
                className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white shrink-0">
                <Link2 className="w-4 h-4 mr-2" /> Crear enlace de pago
            </Button>
            <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
                <DialogContent className="bg-[#111] border-[#333] max-w-md text-white" data-testid="dar-de-alta-dialogo">
                    <DialogHeader>
                        <DialogTitle className="uppercase tracking-wider flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-[#FF671F]" /> {hecho ? (hecho.conEnlace ? 'Enlace de pago listo' : 'Cliente dado de alta') : (form.conEnlace ? 'Crear enlace de pago' : 'Dar de alta un cliente')}
                        </DialogTitle>
                    </DialogHeader>
                    {!hecho ? (
                        <div className="space-y-3">
                            <label className="block text-white/60 text-xs">Nombre
                                <Input value={form.nombre} onChange={campo('nombre')} data-testid="alta-nombre"
                                    className="bg-[#0A0A0A] border-[#333] text-white mt-1" />
                            </label>
                            <label className="block text-white/60 text-xs">Correo
                                <Input type="email" value={form.email} onChange={campo('email')} data-testid="alta-email"
                                    className="bg-[#0A0A0A] border-[#333] text-white mt-1" />
                            </label>
                            <label className="block text-white/60 text-xs">Plan
                                <select value={form.plan} onChange={campo('plan')} className={caja} data-testid="alta-plan">
                                    <option value="">Elige el plan</option>
                                    {/* Solo los planes nuevos (12-10): los antiguos son de quien ya los
                                        tiene. La lista la decide el servidor (lib/planesParaElegir). */}
                                    {planes.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                                </select>
                            </label>
                            <label className="flex items-center gap-2 text-white/80 text-sm">
                                <input type="checkbox" checked={form.conEnlace} onChange={campo('conEnlace')} data-testid="alta-con-enlace" />
                                Cobrar con enlace: la cuenta se crea cuando pague
                            </label>
                            {form.conEnlace ? (
                                <>
                                    {/* «Que me sugiera la fecha pero que me deje corregir» (Francisco,
                                        16-10). Sin tocarla, la cuenta el pago el día que pague. */}
                                    {tramo && (
                                        <label className="block text-white/60 text-xs">Pago
                                            <select value={enTramos ? PAGO_EN_TRAMOS : '12'} onChange={campo('pagoSemanas')}
                                                className={caja} data-testid="alta-pago-semanas">
                                                <option value="12">Pago 12 semanas · {euros(planCatalog?.[form.plan]?.precio)}</option>
                                                <option value={PAGO_EN_TRAMOS}>Pago 4 semanas · {euros(tramo.importe)} cada 4 semanas</option>
                                            </select>
                                        </label>
                                    )}
                                    {!seRenueva && (
                                        <label className="block text-white/60 text-xs">Acceso hasta
                                            <input type="date" value={form.hasta} min={fechas?.empieza || hoyLocal()} onChange={campo('hasta')}
                                                className={caja} data-testid="alta-hasta-enlace" />
                                        </label>
                                    )}
                                    {fechas && (
                                        <p className="text-white/50 text-xs" data-testid="alta-fechas-enlace">
                                            Si paga hoy, empieza el {largo(fechas.empieza)}
                                            {ultimoDia ? <> y tiene acceso hasta el {largo(ultimoDia)}</> : null}.
                                        </p>
                                    )}
                                    {/* Pago recurrente (16-10): Stripe le cobra solo antes de acabar
                                        cada ciclo; si un cobro no entra y Stripe deja de reintentar,
                                        pierde el acceso. Desde el 03-11 (batería de Jesús, 1.3 y 1.5) va
                                        siempre puesto y con el texto «Tu plan se renueva de forma
                                        automática», en vez de «Se renueva solo: Stripe le cobra cada ciclo». */}
                                    {enTramos ? (
                                        <p className="text-white/80 text-sm" data-testid="alta-recurrente-fijo">
                                            Tu plan se renueva de forma automática, cada 4 semanas.
                                        </p>
                                    ) : (
                                        <label className="flex items-center gap-2 text-white/80 text-sm">
                                            <input type="checkbox" checked readOnly disabled data-testid="alta-recurrente" />
                                            Tu plan se renueva de forma automática
                                        </label>
                                    )}
                                    {/* CUÁNDO SE RENUEVA (batería de Jesús, 1.4): seis días antes del
                                        final del acceso. */}
                                    {renueva && (
                                        <p className="text-white/50 text-xs" data-testid="alta-renovacion">
                                            La renovación se cobra seis días antes del final del acceso: el {largo(renueva)}.
                                        </p>
                                    )}
                                </>
                            ) : (
                                <>
                                    <label className="block text-white/60 text-xs">Acceso hasta
                                        <input type="date" value={form.hasta} min={hoyLocal()} onChange={campo('hasta')} disabled={form.cortesia}
                                            className={`${caja} disabled:opacity-40`} data-testid="alta-hasta" />
                                    </label>
                                    <label className="flex items-center gap-2 text-white/80 text-sm">
                                        <input type="checkbox" checked={form.cortesia} onChange={campo('cortesia')} data-testid="alta-cortesia" />
                                        De cortesía, sin fecha de fin
                                    </label>
                                    {fechas && (
                                        <p className="text-white/40 text-xs" data-testid="alta-fechas">
                                            Empieza el {largo(fechas.empieza)}.
                                        </p>
                                    )}
                                </>
                            )}
                            {/* El botón apagado, con su porqué (11-10): antes no decía nada. */}
                            {falta && (
                                <p className="text-amber-300/90 text-xs" data-testid="alta-falta">{falta}</p>
                            )}
                        </div>
                    ) : hecho.conEnlace ? (
                        <div className="space-y-3">
                            <p className="text-white/60 text-sm">Enlace de pago de <span className="text-white font-semibold">{hecho.nombre}</span> ({hecho.email}), {euros(hecho.importe_eur)}{hecho.pago_semanas
                                ? `, y se renueva de forma automática cada ${hecho.pago_semanas} semanas`
                                : hecho.recurrente ? ', y se renueva de forma automática' : ''}:</p>
                            <div className="bg-[#0A0A0A] border border-[#333] rounded-xl p-3 break-all text-[#FF671F] text-xs font-mono" data-testid="alta-enlace-url">
                                {hecho.url}
                            </div>
                            <p className="text-white/60 text-xs">Cópialo y mándaselo por WhatsApp. Cuando pague, su cuenta se crea sola con el plan
                                {hecho.correo_de_alta_a ? <> y la bienvenida, con su contraseña temporal, sale a {hecho.correo_de_alta_a}</> : null}.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-white/60 text-sm">Contraseña temporal de <span className="text-white font-semibold">{hecho.nombre}</span> ({hecho.email}):</p>
                            <div className="bg-[#0A0A0A] border border-[#333] rounded-xl p-4 text-center">
                                <span className="text-[#FF671F] font-mono font-bold text-lg" data-testid="alta-temp-password">{hecho.temp_password}</span>
                            </div>
                            <p className="text-yellow-400/80 text-xs">Solo se muestra ahora. Recomiéndale cambiarla al entrar (Perfil, Cambiar contraseña).</p>
                            {/* La bienvenida: sale en segundo plano al cliente, con los datos del plan
                                y esta contraseña temporal (Francisco, 17-10 noche). */}
                            {hecho.correo_de_alta_a && (
                                <p className="text-white/60 text-xs" data-testid="alta-correo">
                                    La bienvenida, con los datos del plan y esta contraseña, sale ahora a {hecho.correo_de_alta_a}.
                                </p>
                            )}
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        {!hecho ? (
                            <>
                                <Button variant="outline" onClick={cerrar} className="bg-transparent border-[#333] text-white">Cancelar</Button>
                                <Button onClick={crear} disabled={!!falta || guardando} data-testid="alta-crear"
                                    className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white">
                                    {form.conEnlace ? <><Link2 className="w-4 h-4 mr-1" />{guardando ? 'Creando el enlace…' : 'Crear enlace de pago'}</>
                                        : (guardando ? 'Dando de alta…' : 'Dar de alta')}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button onClick={copiar} className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white" data-testid="alta-copiar">Copiar</Button>
                                {!hecho.conEnlace && <Button variant="outline" onClick={() => { const id = hecho.client_id; cerrar(); navigate(`/admin/clients/${id}`); }}
                                    className="bg-transparent border-[#333] text-white" data-testid="alta-ver-ficha">Ver su ficha</Button>}
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DarDeAlta;
